// Theme related sagas

import { put, select } from 'redux-saga/effects';

// Services
import { ErrorCategory, handleError } from '@services/errorService';

// Api
import client from '@api';

// Actions
import {
  UpdateAdvancedColor,
  UpdateMarkup,
  UpdateTheme,
  UserUpdate,
} from '@actions/user';

// Graph
import {
  changeAdvancedColors,
  changeMarkup,
  changeTheme,
} from '@graphql/mutations/account';

// Types
import type {
  AdvancedColor,
  BackgroundSettings,
  PayloadAction,
  SetThemePayload,
} from './types';

export function* SetThemeSaga({
  payload: { isAdvancedColor = false, ...props },
}: PayloadAction<SetThemePayload>): Generator {
  try {
    const { username, userBackgroundSettings, advancedColor } = (yield select(
      (state: {
        User: { username: string; backgroundSettings: BackgroundSettings };
      }) => ({
        username: state.User.username,
        userBackgroundSettings: state.User.backgroundSettings,
        advancedColor: state.User.backgroundSettings.advancedColor,
      }),
    )) as {
      username: string;
      userBackgroundSettings: BackgroundSettings;
      advancedColor: AdvancedColor | undefined;
    };
    const {
      data: {
        changeUserBackgroundSettings: {
          backgroundSettings: backgroundSettingsData,
        },
      },
    } = (yield client().mutate({
      mutation: changeTheme,
      variables: {
        input: { ...props },
      },
    })) as {
      data: {
        changeUserBackgroundSettings: {
          backgroundSettings: BackgroundSettings;
        };
      };
    };

    yield put(UpdateTheme({ ...backgroundSettingsData }));

    if (!isAdvancedColor && advancedColor?.isActive) {
      const { __typename, ...advancedColorProps } = advancedColor;
      const {
        data: {
          changeUserBackgroundSettings: {
            backgroundSettings: advancedColorData,
          },
        },
      } = (yield client().mutate({
        mutation: changeAdvancedColors,
        variables: {
          input: { advancedColor: { ...advancedColorProps, isActive: false } },
        },
      })) as {
        data: {
          changeUserBackgroundSettings: {
            backgroundSettings: { advancedColor: AdvancedColor };
          };
        };
      };

      yield put(UpdateAdvancedColor(advancedColorData.advancedColor));
    }

    yield put(
      UserUpdate({
        username,
        backgroundSettings: {
          ...userBackgroundSettings,
          ...backgroundSettingsData,
        },
      }),
    );
  } catch (e) {
    handleError(e, { category: ErrorCategory.API });
  }
}

export function* SetMarkupSaga({ payload }: PayloadAction<string>): Generator {
  try {
    const {
      data: {
        changeUserBackgroundSettings: { backgroundSettings },
      },
    } = (yield client().mutate({
      mutation: changeMarkup,
      variables: {
        input: { markup: payload },
      },
    })) as {
      data: {
        changeUserBackgroundSettings: {
          backgroundSettings: { markup: string };
        };
      };
    };

    yield put(UpdateMarkup(backgroundSettings?.markup));
  } catch (e) {
    handleError(e, { category: ErrorCategory.API });
  }
}
